import { message } from "antd";
import { BankAccountAPI, BankAccountListAPI } from "./type";
import { API_URL, API_USER_URL } from "@/env";


const API_USE = API_USER_URL || '';

async function getUserBankList(): Promise<BankAccountListAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching bank list');
        }
        const data = await response.json();
        return data as BankAccountListAPI;
    } catch (error) {
        console.error('Failed to fetch bank list:', error);
        return Promise.reject(error);
    }
}

async function getDefaultUserBankAccount(): Promise<BankAccountAPI> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
        });
        if (!response.ok) {
            message.error('Error fetching default bank account');
        }
        const data: BankAccountListAPI = await response.json();
        const defaultAccount = data.data.find(account => account.is_default);
        if (!defaultAccount) {
            return data.data[0] || null;
        }
        return defaultAccount;
    } catch (error) {
        console.error('Failed to fetch default bank account:', error);
        return Promise.reject(error);
    }
}

async function addUserBankAccount(otp: string, bank_name: string, account_number: string, account_holder_name: string, branch: string, is_default: boolean): Promise<any> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
                otp,
                bank_name,
                account_number,
                account_holder_name,
                branch,
                is_default
            })
        });
        if (!response.ok) {
            message.error('Error adding bank account');
        }
        const data = await response.json();
        if (data.status) {
            message.success('Bank account added successfully');
        }
        else {
            message.error('Failed to add bank account' + (data.message ? `: ${data.message}` : ''));
        }
        return data;
    } catch (error) {
        console.error('Failed to add bank account:', error);
        return Promise.reject(error);
    }
}

async function bankRequestOTP(): Promise<any> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts/request-otp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
        });
        if (!response.ok) {
            message.error('Error requesting OTP');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to request OTP:', error);
        return Promise.reject(error);
    }   
}

async function updateUserBankAccount(id: number, otp: string, bank_name: string, account_number: string, account_holder_name: string, branch: string, is_default: boolean): Promise<any> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
                otp,
                bank_name,
                account_number,
                account_holder_name,
                branch,
                is_default
            })
        });
        if (!response.ok) {
            message.error('Error updating bank account');
        }
        const data = await response.json();
        if (data.status) {
            message.success('Bank account updated successfully');
        }
        else {
            message.error('Failed to update bank account' + (data.message ? `: ${data.message}` : ''));
        }
        return data;
    } catch (error) {
        console.error('Failed to update bank account:', error);
        return Promise.reject(error);
    }
}

async function deleteUserBankAccount(id: number, otp: string): Promise<any> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ otp })
        });
        if (!response.ok) {
            message.error('Error deleting bank account');
        }
        const data = await response.json();
        if (data.status) {
            message.success('Bank account deleted successfully');
        }
        else {
            message.error('Failed to delete bank account' + (data.message ? `: ${data.message}` : ''));
        }
        return data;
    } catch (error) {
        console.error('Failed to delete bank account:', error);
        return Promise.reject(error);
    }
}

async function setDefaultBankAccount(id: number, otp: string): Promise<any> {
    const token = localStorage.getItem('token') || '';
    if (!token) {
        message.error('No authentication token found');
        return Promise.reject('No authentication token found');
    }

    try {
        const response = await fetch(`${API_USE}/api/v1/be/user-bank-accounts/${id}/set-default`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ otp })
        });
        if (!response.ok) {
            message.error('Error setting default bank account');
        }
        const data = await response.json();
        if (data.status) {
            message.success('Default bank account set successfully');
        }
        else {
            message.error('Failed to set default bank account' + (data.message ? `: ${data.message}` : ''));
        }
        return data;
    } catch (error) {
        console.error('Failed to set default bank account:', error);
        return Promise.reject(error);
    }
}

export {
    getUserBankList,
    getDefaultUserBankAccount,
    addUserBankAccount,
    bankRequestOTP,
    updateUserBankAccount,
    deleteUserBankAccount,
    setDefaultBankAccount
};